import React, { useEffect, useMemo, useState } from "react";
import {
  Box,
  Button,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  Grid,
  TextField,
  Typography,
} from "@mui/material";

const READ_ONLY_FIELDS = new Set(["createdAt", "updatedAt"]);

const DEFAULT_FIELD_ORDER = [
  "name",
  "firstName",
  "lastName",
  "email",
  "createdAt",
  "updatedAt",
];

const toDisplayValue = (value) => {
  if (value == null) return "";
  // Firestore Timestamp
  if (typeof value === "object" && typeof value.toDate === "function") {
    try {
      return value.toDate().toLocaleString();
    } catch {
      return "";
    }
  }
  // JS Date
  if (value instanceof Date) {
    return value.toLocaleString();
  }
  return String(value);
};

const getFieldLabel = (key) => {
  switch (key) {
    case "name":
      return "Name";
    case "firstName":
      return "First Name";
    case "lastName":
      return "Last Name";
    case "email":
      return "Email Address";
    case "createdAt":
      return "Create Date";
    case "updatedAt":
      return "Last Updated";
    default:
      return key;
  }
};

/**
 * record: { id, ...fields }
 */
const GuestContributorEditModal = ({ open, onClose, record, onSave }) => {
  const [draft, setDraft] = useState({});

  useEffect(() => {
    if (open && record) {
      // Exclude `id` from modal per requirements
      const { id: _id, createdBy: _createdBy, ...rest } = record;
      // Ensure all expected fields exist so the modal always shows them
      const next = { ...rest };
      DEFAULT_FIELD_ORDER.forEach((k) => {
        if (next[k] === undefined) next[k] = "";
      });
      setDraft(next);
    }
  }, [open, record]);

  const fields = useMemo(() => {
    if (!record) return [];
    // Always show all expected fields
    return DEFAULT_FIELD_ORDER;
  }, [record]);

  const handleChange = (key) => (e) => {
    setDraft((prev) => ({ ...prev, [key]: e.target.value }));
  };

  const handleSave = async () => {
    if (!record?.id) return;
    // Only update non-read-only fields
    const payload = {};
    Object.entries(draft).forEach(([k, v]) => {
      if (READ_ONLY_FIELDS.has(k)) return;
      if (k === "createdBy") return;
      payload[k] = v;
    });
    await onSave(record.id, payload, { ...record, ...draft });
  };

  return (
    <Dialog open={open} onClose={onClose} maxWidth="sm" fullWidth>
      <DialogTitle>Edit Guest Contributor</DialogTitle>
      <DialogContent>
        {!record ? (
          <Typography sx={{ py: 2 }}>No record selected.</Typography>
        ) : (
          <Box sx={{ pt: 1 }}>
            <Grid container spacing={2}>
              {fields.map((key) => {
                const isReadOnly = READ_ONLY_FIELDS.has(key);
                const value = isReadOnly ? toDisplayValue(record[key]) : draft[key] ?? "";
                return (
                  <Grid item xs={12} key={key}>
                    <TextField
                      fullWidth
                      label={getFieldLabel(key)}
                      InputLabelProps={{ shrink: true }}
                      value={value}
                      onChange={isReadOnly ? undefined : handleChange(key)}
                      InputProps={{ readOnly: isReadOnly }}
                      variant="outlined"
                    />
                  </Grid>
                );
              })}
            </Grid>
          </Box>
        )}
      </DialogContent>
      <DialogActions>
        <Button onClick={onClose} sx={{ textTransform: "none" }}>
          Cancel
        </Button>
        <Button
          onClick={handleSave}
          variant="contained"
          sx={{ textTransform: "none", backgroundColor: "#00bcd4" }}
          disabled={!record}
        >
          Save
        </Button>
      </DialogActions>
    </Dialog>
  );
};

export default GuestContributorEditModal;
